import { Users2 } from 'lucide-react'
import React, { useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useBulkCreateProspectEmployees } from 'components/hooks/quotes/useBulkCreateProspectEmployees'
import { useCreateManualCensus } from 'components/hooks/quotes/useCreateManualCensus'
import { Banner, TextButton } from 'slab'
import { capitalize } from 'utils'

import { EmployeeInfoEntry } from './EmployeeInfoEntry'

export type DependentInfo = {
  relationship: 'spouse' | 'child' | 'self'
  date_of_birth: string
}

export type EmployeeInfo = {
  first_name?: string
  last_name?: string
  email?: string
  name?: string | null
  date_of_birth: string
  zip: string
  dependents: DependentInfo[]
}

export type Employees = {
  employees: EmployeeInfo[]
}

type NavButtonRenderProp = (props: {
  onSubmit: () => Promise<void>
  isSubmitDisabled: boolean
}) => React.ReactNode

type Props = {
  prospectId: string;
  existingCensusId?: string;
  existingEmployees?: EmployeeInfo[];
  onEmployeesAdded: (censusId: string) => Promise<void>;
  renderNavButtons: NavButtonRenderProp;
}

export function EmployeeEntry ({ prospectId, existingCensusId, existingEmployees, onEmployeesAdded, renderNavButtons }: Props) {
  const [error, setError] = useState<GrapeAPIErrorType>()
  const [defaultCardsToOpen, setDefaultCardsToOpen] = useState(!existingEmployees?.length)

  const createManualCensusMutation = useCreateManualCensus({ prospectId })
  const bulkCreateProspectEmployeesMutation = useBulkCreateProspectEmployees({ prospectId })

  const methods = useForm<Employees>({
    defaultValues: {
      employees: existingEmployees || [
        {
          first_name: '',
          last_name: '',
          email: '',
          name: '',
          date_of_birth: '',
          zip: '',
          dependents: []
        }
      ]
    }
  })

  const { fields: employeeFields, append: appendEmployee, remove: removeEmployee } = useFieldArray({
    control: methods.control,
    name: 'employees'
  })

  const onSubmit = async (data: Employees) => {
    let censusId: string
    try {
      if (existingCensusId && existingEmployees?.length) {
        censusId = existingCensusId
      } else {
        const census = await createManualCensusMutation.mutateAsync()
        censusId = census.id
      }

      // Transform data to include concatenated name and duplicate email
      const transformedEmployees = data.employees.map(employee => {
        const { email: _email, ...employeeWithoutEmail } = employee
        return {
          ...employeeWithoutEmail,
          // Temporarily still send name while we iron out potential bugs
          name: `${employee.first_name} ${employee.last_name}`.trim(),
          work_email: employee.email || undefined,
          personal_email: employee.email || undefined,
        }
      })

      await bulkCreateProspectEmployeesMutation.mutateAsync({
        census_id: censusId,
        employees: transformedEmployees,
        overwrite_existing: true,
      })
      await onEmployeesAdded(censusId)
    } catch (error) {
      const apiError = error as GrapeAPIErrorType
      if (apiError.fieldErrors && Object.keys(apiError.fieldErrors).length > 0) {
        setError(undefined)
        Object.entries(apiError.fieldErrors).forEach(([field, messages]) => {
          const [, employeeIndex, fieldName] = field.split('.')
          if (employeeIndex && fieldName) {
            methods.setError(`employees.${parseInt(employeeIndex)}.${fieldName as keyof EmployeeInfo}`, {
              type: 'server',
              message: `${capitalize(fieldName)} ${messages[0]}`
            })
          }
        })
      } else {
        setError(apiError)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <div className='flex flex-col gap-6'>
        {employeeFields.map((employee, index) => (
          <EmployeeInfoEntry
            key={employee.id}
            index={index}
            defaultOpen={defaultCardsToOpen}
            onRemove={() => removeEmployee(index)}
          />
        ))}
        <div className='mb-4'>
          <TextButton
            leftIcon={Users2}
            color='purple'
            onClick={() => {
              setDefaultCardsToOpen(true)
              appendEmployee({ first_name: '', last_name: '', email: '', name: '', date_of_birth: '', zip: '', dependents: [] })
            }}
          >
            Add {employeeFields.length ? 'another' : ''} employee
          </TextButton>
        </div>
        {error && (
          <Banner
            title='Error creating employees'
            type='critical'
            onDismiss={() => setError(undefined)}
          >
            {error.message} - {error.errors?.join(', ')}
          </Banner>
        )}
        {renderNavButtons({
          onSubmit: methods.handleSubmit(onSubmit),
          isSubmitDisabled: !employeeFields.length || methods.formState.isSubmitting,
        })}
      </div>
    </FormProvider>
  )
}
